import { useState, useCallback, useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  dadanExportStatusAtom,
  dadanExportProgressAtom,
  dadanExportJobIdAtom,
  dadanExportChaptersEnabledAtom,
  dadanExportQualityAtom,
  updateDadanExportProgressAtom,
  completeDadanExportAtom,
  resetDadanExportStateAtom,
  isDadanExportPopoverOpenAtom,
  dadanExportProjectIdAtom,
  DadanExportStatus,
} from '../atoms/exportAtoms';
import { activeProjectAtom, isProjectLoadingAtom } from '../atoms/sceneAtoms';
import { DadanExportService } from '../services/dadanExportService';
import logger from '../utils/logger';

const PROGRESS_STORAGE_KEY = 'dadan_export_progress_';
const SSE_MAX_RECONNECT_FAILURES = 5;
const FALLBACK_POLL_INTERVAL = 10000;

function getStoredProgress(jobId: string): number {
  try {
    const stored = localStorage.getItem(PROGRESS_STORAGE_KEY + jobId);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
}

function setStoredProgress(jobId: string, progress: number): void {
  try {
    localStorage.setItem(PROGRESS_STORAGE_KEY + jobId, String(progress));
  } catch {
  }
}

function clearStoredProgress(jobId: string): void {
  try {
    localStorage.removeItem(PROGRESS_STORAGE_KEY + jobId);
  } catch {
  }
}

const STATUS_MAP: Record<string, DadanExportStatus> = {
  pending: 'queued',
  queued: 'queued',
  processing: 'stitching',
  stitching: 'stitching',
  uploading: 'uploading',
  completed: 'complete',
  failed: 'failed',
};

function clampProgress(currentProgress: number, serverProgress: number, serverStatus: string): number {
  let maxProgress = 99;
  if (serverStatus === 'completed') {
    maxProgress = 100;
  } else if (serverStatus === 'uploading') {
    maxProgress = 99;
  } else if (['processing', 'stitching'].includes(serverStatus)) {
    maxProgress = 89;
  }
  return Math.round(Math.min(maxProgress, Math.max(currentProgress, serverProgress || 0)));
}

export function useDadanExport() {
  const project = useAtomValue(activeProjectAtom);
  const isProjectLoading = useAtomValue(isProjectLoadingAtom);
  const chaptersEnabled = useAtomValue(dadanExportChaptersEnabledAtom);
  const quality = useAtomValue(dadanExportQualityAtom);
  
  const status = useAtomValue(dadanExportStatusAtom);
  const progress = useAtomValue(dadanExportProgressAtom);
  const [jobId, setJobId] = useAtom(dadanExportJobIdAtom);
  
  const updateProgress = useSetAtom(updateDadanExportProgressAtom);
  const completeExport = useSetAtom(completeDadanExportAtom);
  const resetState = useSetAtom(resetDadanExportStateAtom);
  const setPopoverOpen = useSetAtom(isDadanExportPopoverOpenAtom);
  const setExportProjectId = useSetAtom(dadanExportProjectIdAtom);
  
  const [isStarting, setIsStarting] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const fallbackPollRef = useRef<NodeJS.Timeout | null>(null);
  const interpolationRef = useRef<NodeJS.Timeout | null>(null);
  const currentProgressRef = useRef<number>(0);
  const lastServerProgressRef = useRef<number>(0);
  const sseFailureCountRef = useRef<number>(0);
  const handleJobUpdateRef = useRef<(job: any, isTerminal?: boolean) => void>(() => {});
  
  currentProgressRef.current = progress;

  const stopInterpolation = useCallback(() => {
    if (interpolationRef.current) {
      clearInterval(interpolationRef.current);
      interpolationRef.current = null;
    }
  }, []);

  const stopFallbackPolling = useCallback(() => {
    if (fallbackPollRef.current) {
      clearInterval(fallbackPollRef.current);
      fallbackPollRef.current = null;
    }
  }, []);

  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const stopListening = useCallback(() => {
    closeEventSource();
    stopFallbackPolling();
    stopInterpolation();
    sseFailureCountRef.current = 0;
  }, [closeEventSource, stopFallbackPolling, stopInterpolation]);

  const startInterpolation = useCallback(() => {
    stopInterpolation();
    
    const INTERPOLATION_INTERVAL = 1500;
    const MAX_INTERPOLATED_PROGRESS = 40;
    const INCREMENT = 1.5;
    
    interpolationRef.current = setInterval(() => {
      const current = currentProgressRef.current;
      const serverProgress = lastServerProgressRef.current;
      
      if (serverProgress >= current || serverProgress >= MAX_INTERPOLATED_PROGRESS) {
        stopInterpolation();
        return;
      }
      
      if (current < MAX_INTERPOLATED_PROGRESS) {
        const newProgress = Math.round(Math.min(current + INCREMENT, MAX_INTERPOLATED_PROGRESS));
        updateProgress({
          status: 'queued',
          progress: newProgress,
          message: getProgressMessage('queued', newProgress),
        });
      }
    }, INTERPOLATION_INTERVAL);
  }, [stopInterpolation, updateProgress]);

  const handleJobUpdate = useCallback((job: any, isTerminal: boolean = false) => {
    const uiStatus = STATUS_MAP[job.status] || 'stitching';
    
    lastServerProgressRef.current = job.progress || 0;
    
    const isStillWaiting = ['queued', 'pending'].includes(job.status);
    if (!isStillWaiting) {
      stopInterpolation();
    }
    
    const clamped = clampProgress(currentProgressRef.current, job.progress || 0, job.status);
    
    if (job.id) {
      setStoredProgress(job.id, clamped);
    }
    
    updateProgress({
      status: uiStatus,
      progress: clamped,
      message: getProgressMessage(uiStatus, clamped),
    });

    if (job.status === 'completed' && job.videoId) {
      stopListening();
      if (job.id) clearStoredProgress(job.id);
      completeExport({
        success: true,
        videoId: job.videoId,
        watchUrl: `https://app.dadan.io/video/${job.videoId}`,
        shareUrl: `https://app.dadan.io/share/${job.videoId}`,
      });
    } else if (job.status === 'completed' && !job.videoId && job.id) {
      logger.warn('[useDadanExport] Completed without videoId, fetching from status endpoint');
      DadanExportService.getExportStatus(job.id).then((resp) => {
        if (resp.success && resp.job?.videoId) {
          stopListening();
          clearStoredProgress(job.id);
          completeExport({
            success: true,
            videoId: resp.job.videoId,
            watchUrl: `https://app.dadan.io/video/${resp.job.videoId}`,
            shareUrl: `https://app.dadan.io/share/${resp.job.videoId}`,
          });
        } else {
          logger.error('[useDadanExport] Status endpoint also missing videoId');
          stopListening();
          clearStoredProgress(job.id);
          completeExport({
            success: false,
            error: 'Export completed but video ID is missing',
          });
        }
      }).catch(() => {
        stopListening();
        clearStoredProgress(job.id);
        completeExport({
          success: false,
          error: 'Export completed but failed to retrieve video details',
        });
      });
    } else if (job.status === 'failed') {
      stopListening();
      if (job.id) clearStoredProgress(job.id);
      completeExport({
        success: false,
        error: job.error || 'Export failed',
      });
    } else if (job.status === 'cancelled') {
      stopListening();
      if (job.id) clearStoredProgress(job.id);
      resetState();
    }
  }, [updateProgress, completeExport, stopListening, stopInterpolation, resetState]);

  handleJobUpdateRef.current = handleJobUpdate;

  const startFallbackPolling = useCallback((exportJobId: string) => {
    stopFallbackPolling();
    logger.warn('[useDadanExport] SSE unavailable, falling back to polling');

    fallbackPollRef.current = setInterval(async () => {
      try {
        const response = await DadanExportService.getExportStatus(exportJobId);
        if (response.success && response.job) {
          handleJobUpdate(response.job);
        }
      } catch (error) {
        logger.error('[useDadanExport] Fallback poll error:', error);
      }
    }, FALLBACK_POLL_INTERVAL);
  }, [stopFallbackPolling, handleJobUpdate]);

  const startListeningForUpdates = useCallback((exportJobId: string) => {
    closeEventSource();
    stopFallbackPolling();
    sseFailureCountRef.current = 0;

    const isDev = process.env.NODE_ENV === 'development';
    if (isDev) {
      logger.info(`[useDadanExport] Dev environment detected, using polling for job: ${exportJobId}`);
      startFallbackPolling(exportJobId);
      return;
    }

    logger.info(`[useDadanExport] Connecting SSE for job: ${exportJobId}`);

    let sseDataReceived = false;
    const sseTimeoutId = setTimeout(() => {
      if (!sseDataReceived) {
        logger.warn('[useDadanExport] SSE timeout - no data received in 5s, switching to polling');
        closeEventSource();
        startFallbackPolling(exportJobId);
      }
    }, 5000);

    const eventSource = DadanExportService.connectToExportStream(exportJobId, {
      onSnapshot: (job) => {
        logger.info(`[useDadanExport] SSE snapshot: status=${job.status}, progress=${job.progress}`);
        sseDataReceived = true;
        clearTimeout(sseTimeoutId);
        sseFailureCountRef.current = 0;
        handleJobUpdateRef.current(job);
      },
      onProgress: (job) => {
        sseDataReceived = true;
        clearTimeout(sseTimeoutId);
        sseFailureCountRef.current = 0;
        handleJobUpdateRef.current(job);
      },
      onComplete: (job) => {
        sseDataReceived = true;
        clearTimeout(sseTimeoutId);
        sseFailureCountRef.current = 0;
        handleJobUpdateRef.current(job);
      },
      onFailed: (job) => {
        sseDataReceived = true;
        clearTimeout(sseTimeoutId);
        sseFailureCountRef.current = 0;
        handleJobUpdateRef.current(job);
      },
      onCancelled: (job) => {
        sseDataReceived = true;
        clearTimeout(sseTimeoutId);
        sseFailureCountRef.current = 0;
        handleJobUpdateRef.current(job);
      },
      onError: (_error) => {
        sseFailureCountRef.current++;
        logger.warn(`[useDadanExport] SSE error (attempt ${sseFailureCountRef.current}/${SSE_MAX_RECONNECT_FAILURES})`);

        if (sseFailureCountRef.current >= SSE_MAX_RECONNECT_FAILURES) {
          logger.error('[useDadanExport] SSE max reconnect failures reached, switching to polling');
          clearTimeout(sseTimeoutId);
          closeEventSource();
          startFallbackPolling(exportJobId);
        }
      },
    });

    eventSourceRef.current = eventSource;
  }, [closeEventSource, stopFallbackPolling, startFallbackPolling]);

  const startExport = useCallback(async () => {
    if (!project || !project.id) {
      logger.error('[useDadanExport] No project to export');
      return;
    }

    setIsStarting(true);
    updateProgress({
      status: 'preparing',
      progress: 5,
      message: 'Initializing export...',
    });

    try {
      const response = await DadanExportService.startExport({
        projectId: Number(project.id),
        projectTitle: project.title || 'Untitled Project',
        options: {
          includeChapters: chaptersEnabled,
          quality,
        },
      });

      if (!response.success || !response.jobId) {
        throw new Error(response.error || 'Failed to start export');
      }

      setJobId(response.jobId);
      setExportProjectId(Number(project.id));
      updateProgress({
        status: 'queued',
        progress: 10,
        message: 'Export queued for processing...',
      });

      startInterpolation();
      startListeningForUpdates(response.jobId);

    } catch (error: any) {
      logger.error('[useDadanExport] Failed to start export:', error);
      completeExport({
        success: false,
        error: error.message || 'Failed to start export',
      });
    } finally {
      setIsStarting(false);
    }
  }, [project, chaptersEnabled, quality, updateProgress, setJobId, setExportProjectId, completeExport, startInterpolation, startListeningForUpdates]);

  const lastProjectIdRef = useRef<number | null>(null);

  useEffect(() => {
    const currentProjectId = project?.id ? Number(project.id) : null;
    
    if (lastProjectIdRef.current !== null && currentProjectId !== lastProjectIdRef.current) {
      logger.info(`[useDadanExport] Project changed from ${lastProjectIdRef.current} to ${currentProjectId}, resetting export state`);
      stopListening();
      resetState();
      currentProgressRef.current = 0;
      lastServerProgressRef.current = 0;
    }
    
    lastProjectIdRef.current = currentProjectId;
  }, [project?.id, stopListening, resetState]);

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  useEffect(() => {
    if (isProjectLoading) {
      return;
    }
    
    const currentProjectId = project?.id ? Number(project.id) : null;
    
    const recoverActiveExport = async () => {
      if (!currentProjectId || jobId || status !== 'idle') {
        return;
      }

      try {
        const response = await DadanExportService.getLatestExportJob(currentProjectId);
        
        if (!response.success || !response.job) {
          return;
        }

        const { job } = response;
        
        if (job.projectId && Number(job.projectId) !== currentProjectId) {
          logger.warn(`[useDadanExport] Job ${job.id} belongs to project ${job.projectId}, not current project ${currentProjectId} - skipping`);
          return;
        }
        
        const jobAge = Date.now() - new Date(job.createdAt).getTime();
        const MAX_JOB_AGE = 24 * 60 * 60 * 1000;
        
        if (jobAge > MAX_JOB_AGE) {
          logger.info(`[useDadanExport] Found old export job, ignoring: ${job.id}`);
          return;
        }

        logger.info(`[useDadanExport] Recovering export job: ${job.id}, status: ${job.status}, projectId: ${job.projectId}`);
        
        if (job.isActive) {
          setJobId(job.id);
          setExportProjectId(currentProjectId);
          setPopoverOpen(true);
          
          const uiStatus = STATUS_MAP[job.status] || 'stitching';
          
          const storedProgress = getStoredProgress(job.id);
          const serverProgress = job.progress || 0;
          const recoveredProgress = Math.max(storedProgress, serverProgress);
          
          currentProgressRef.current = recoveredProgress;
          lastServerProgressRef.current = serverProgress;
          
          updateProgress({
            status: uiStatus,
            progress: recoveredProgress,
            message: getProgressMessage(uiStatus, recoveredProgress),
          });

          startListeningForUpdates(job.id);
          
          logger.info(`[useDadanExport] Resumed SSE for job: ${job.id}, recovered progress: ${recoveredProgress} (stored: ${storedProgress}, server: ${serverProgress})`);
        } else if (job.status === 'completed') {
          logger.info(`[useDadanExport] Found completed export: ${job.id} - not recovering (user can re-export)`);
        } else if (job.status === 'failed') {
          setJobId(job.id);
          setExportProjectId(currentProjectId);
          setPopoverOpen(true);
          completeExport({
            success: false,
            error: job.error || 'Export failed',
          });
          logger.info(`[useDadanExport] Recovered failed export: ${job.id}`);
        }
      } catch (error) {
        logger.error('[useDadanExport] Error recovering export job:', error);
      }
    };

    recoverActiveExport();
  }, [isProjectLoading, project?.id, jobId, status, setJobId, setExportProjectId, setPopoverOpen, updateProgress, completeExport, startListeningForUpdates]);

  const retryExport = useCallback(async () => {
    setJobId(null);
    currentProgressRef.current = 0;
    lastServerProgressRef.current = 0;
    stopListening();
    
    await startExport();
  }, [setJobId, stopListening, startExport]);

  return {
    startExport,
    retryExport,
    isStarting,
    status,
    progress,
    jobId,
    resetState,
  };
}

function getProgressMessage(status: DadanExportStatus, progress: number): string {
  switch (status) {
    case 'preparing':
      return 'Initializing export...';
    case 'queued':
      return 'Waiting in queue...';
    case 'stitching':
      return 'Processing video...';
    case 'uploading':
      return 'Uploading to cloud...';
    case 'complete':
      return 'Export complete!';
    case 'failed':
      return 'Export failed';
    default:
      return 'Processing...';
  }
}
